import { useForm } from '@mantine/form';
import { Button, Group, Stack, TextInput } from '@mantine/core';
import React from 'react';

export interface MutateParkingServiceParkingSpacesFormValues {
  availableCarSpaces: string;
  totalCarSpaces: string;
  availableMotoSpaces: string;
  totalMotoSpaces: string;
  totalHeavyMotoSpaces: string;
  totalDisabledParkingSpaces: string;
  totalMaternityParkingSpaces: string;
}

interface MutateParkingServiceParkingSpacesFormProps {
  initialValues: MutateParkingServiceParkingSpacesFormValues;
  onSubmit: (formValues: MutateParkingServiceParkingSpacesFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
}

const nonNegativeIntegerOrEmpty = (value: string) => {
  if (value === '') return null;
  const num = Number(value);
  return !isNaN(num) && Number.isInteger(num) && num >= 0
    ? null
    : '只能為留空（未知）、0或正整數';
};

export default function MutateParkingServiceParkingSpacesForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  submitButtonText,
}: MutateParkingServiceParkingSpacesFormProps) {
  const form = useForm<MutateParkingServiceParkingSpacesFormValues>({
    mode: 'controlled',
    initialValues: {
      availableCarSpaces: initialValues.availableCarSpaces,
      totalCarSpaces: initialValues.totalCarSpaces,
      availableMotoSpaces: initialValues.availableMotoSpaces,
      totalMotoSpaces: initialValues.totalMotoSpaces,
      totalHeavyMotoSpaces: initialValues.totalHeavyMotoSpaces,
      totalDisabledParkingSpaces: initialValues.totalDisabledParkingSpaces,
      totalMaternityParkingSpaces: initialValues.totalMaternityParkingSpaces,
    },
    validate: {
      availableCarSpaces: nonNegativeIntegerOrEmpty,
      totalCarSpaces: nonNegativeIntegerOrEmpty,
      availableMotoSpaces: nonNegativeIntegerOrEmpty,
      totalMotoSpaces: nonNegativeIntegerOrEmpty,
      totalHeavyMotoSpaces: nonNegativeIntegerOrEmpty,
      totalDisabledParkingSpaces: nonNegativeIntegerOrEmpty,
      totalMaternityParkingSpaces: nonNegativeIntegerOrEmpty,
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Stack gap={16}>
          <Group gap={16}>
            {/* 即時自小客車位 */}
            <TextInput
              label={'即時自小客車位'}
              disabled
              styles={{
                label: { paddingBottom: 4, lineHeight: '20px' },
                input: { width: 305.5 },
              }}
            />
            {/* 即時普通重型機車位 */}
            <TextInput
              label={'即時普通重型機車位'}
              disabled
              styles={{
                label: { paddingBottom: 4, lineHeight: '20px' },
                input: { width: 305.5 },
              }}
            />
          </Group>

          <Group gap={16}>
            {/* 自小客車位 */}
            <TextInput
              label={'自小客車位'}
              placeholder={'請輸入自小客車位數量'}
              key={form.key('totalCarSpaces')}
              {...form.getInputProps('totalCarSpaces')}
              styles={{
                label: { paddingBottom: 4, lineHeight: '20px' },
                input: { width: 305.5 },
              }}
            />
            {/* 普通重型機車位 */}
            <TextInput
              label={'普通重型機車位'}
              placeholder={'請輸入普通重型機車位數量'}
              key={form.key('totalMotoSpaces')}
              {...form.getInputProps('totalMotoSpaces')}
              styles={{
                label: { paddingBottom: 4, lineHeight: '20px' },
                input: { width: 305.5 },
              }}
            />
          </Group>

          <Group gap={16}>
            {/* 大型重型機車位 */}
            <TextInput
              label={'大型重型機車位'}
              placeholder={'請輸入大型重型機車位數量'}
              key={form.key('totalHeavyMotoSpaces')}
              {...form.getInputProps('totalHeavyMotoSpaces')}
              styles={{
                label: { paddingBottom: 4, lineHeight: '20px' },
                input: { width: 305.5 },
              }}
            />
            {/* 身心障礙車位 */}
            <TextInput
              label={'身心障礙車位'}
              placeholder={'請輸入身心障礙車位數量'}
              key={form.key('totalDisabledParkingSpaces')}
              {...form.getInputProps('totalDisabledParkingSpaces')}
              styles={{
                label: { paddingBottom: 4, lineHeight: '20px' },
                input: { width: 305.5 },
              }}
            />
          </Group>

          {/* 孕婦／親子車位 */}
          <TextInput
            label={'孕婦／親子車位'}
            placeholder={'請輸入孕婦／親子車位數量'}
            key={form.key('totalMaternityParkingSpaces')}
            {...form.getInputProps('totalMaternityParkingSpaces')}
            styles={{
              label: { paddingBottom: 4, lineHeight: '20px' },
              input: { width: 305.5 },
            }}
          />
        </Stack>

        {/* 取消 與 新增 or 更新按鈕 */}
        <Group justify={'flex-end'} mt={'24px'}>
          <Button variant="outline" onClick={handleCancel}>
            關閉
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            {submitButtonText}
          </Button>
        </Group>
      </form>
    </>
  );
}
